import React, { useState, useEffect, useRef } from 'react';
import { Upload, Play, Loader, AlertCircle, Download, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/formatDate';
import { formatRuntimeDisplay } from '../utils/runtimeFormatter';
import './AdminPages.css';

const BatchExtract = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [model, setModel] = useState('bedrock-vl');
  const [reportType, setReportType] = useState('SIP APPLICATION FORM (BAJAJ)');
  const [uploading, setUploading] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [batches, setBatches] = useState([]);
  const [batchPagination, setBatchPagination] = useState({ total: 0, skip: 0, limit: 25, page: 1, pages: 1 });
  const [queueStatus, setQueueStatus] = useState(null);
  const [queueItems, setQueueItems] = useState([]);
  const [redirectCountdown, setRedirectCountdown] = useState(0);
  const pollRef = useRef(null);
  const queuePollRef = useRef(null);
  const countdownRef = useRef(null);

  // Load batch history on mount
  useEffect(() => {
    loadBatches();
    loadQueueStatus();
    // Poll queue status every 3 seconds
    queuePollRef.current = setInterval(loadQueueStatus, 3000);
    return () => clearInterval(queuePollRef.current);
  }, []);

  const loadQueueStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch('/api/batch-queue/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setQueueStatus(data.queue || data);
      }
      // Also load queue items
      const respItems = await fetch('/api/batch-queue/list', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (respItems.ok) {
        const dataItems = await respItems.json();
        setQueueItems(dataItems.data || []);
      }
    } catch (err) {
      console.error('Failed to load queue status:', err);
    }
  };

  const loadBatches = async (skip = 0, searchTerm = '') => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ skip, limit: 25 });
      if (searchTerm) params.append('search', searchTerm);
      const resp = await fetch(`/api/batch?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (resp.ok) {
        const data = await resp.json();
        setBatches(data.data || data);
        if (data.pagination) setBatchPagination(data.pagination);
      }
    } catch (err) { console.error(err); }
  };

  const deleteBatch = async (id) => {
    if (!window.confirm(`Delete batch ${id} and ALL its work orders? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`/api/batch/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (resp.ok) {
        setBatches(batches.filter(b => b.batchId !== id));
        if (batchId === id) { setBatchId(null); setStatus(null); }
      }
    } catch (err) { alert('Delete failed: ' + err.message); }
  };

  const processNextBatch = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch('/api/batch-queue/process-next', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await resp.json();

      if (resp.ok) {
        alert(`✅ Started processing: ${data.batchId}`);
        loadQueueStatus(); // Refresh queue status
        loadBatches(); // Refresh batch list
      } else {
        alert(`❌ ${data.error}: ${data.message}`);
      }
    } catch (err) {
      alert('Failed to process next batch: ' + err.message);
    }
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!selected.name.endsWith('.zip')) {
      setError('Please upload a .zip file containing PDFs');
      return;
    }
    setFile(selected);
    setError('');
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setBatchId(null);
    setStatus(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('model', model);
      formData.append('reportType', reportType);

      const resp = await fetch('/api/batch/upload-zip', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await resp.json();
      if (resp.ok) {
        setBatchId(data.batchId);
        setStatus(data);
        startPolling(data.batchId, token);

        // ✨ Show success message with countdown, then navigate
        setRedirectCountdown(3);

        // Countdown timer
        let count = 3;
        countdownRef.current = setInterval(() => {
          count -= 1;
          setRedirectCountdown(count);

          if (count <= 0) {
            clearInterval(countdownRef.current);
            navigate(`/batch/${data.batchId}`);
          }
        }, 1000);
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (err) {
      setError('Failed to upload: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const startPolling = (id, token) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const resp = await fetch(`/api/batch/${id}/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setStatus(data);
          const currentStatus = (data.status || '').toUpperCase();
          if (currentStatus === 'COMPLETED' || currentStatus === 'FAILED') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            // Reload batch list to update history
            loadBatches();
          }
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);


  return (
    <div className="admin-page">
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{ padding: 12, background: 'linear-gradient(135deg, #3182ce 0%, #2c5aa0 100%)', borderRadius: 10, boxShadow: '0 4px 15px rgba(49, 130, 206, 0.3)' }}>
            <Upload size={24} style={{ color: 'white' }} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: '#1a202c' }}>Batch Extract & Processing</h1>
            <p style={{ margin: '4px 0 0 0', color: '#718096', fontSize: 14 }}>Upload ZIP files with PDFs • Sequential processing • Auto-generated reports</p>
          </div>
        </div>
      </div>

      <div className="admin-content">
        {/* Upload Section */}
        <div style={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          borderRadius: 14,
          padding: 28,
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          border: '1px solid rgba(255,255,255,0.5)',
          marginBottom: 24
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 18, fontWeight: 700, color: '#1a202c' }}>📤 Upload & Configure</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div
              onClick={() => !uploading && !(status && status.status === 'processing') && document.getElementById('zip-upload').click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#3182ce'; e.currentTarget.style.background = 'rgba(49, 130, 206, 0.05)'; }}
              onDragLeave={(e) => { e.currentTarget.style.borderColor = file ? '#c6f6d5' : '#e2e8f0'; e.currentTarget.style.background = file ? '#f0fff4' : 'white'; }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = file ? '#c6f6d5' : '#e2e8f0';
                e.currentTarget.style.background = file ? '#f0fff4' : 'white';
                const f = e.dataTransfer.files[0];
                if (f && f.name.endsWith('.zip')) { handleFileSelect({ target: { files: [f] } }); }
                else { setError('Please drop a .zip file'); }
              }}
              style={{
                border: '2px dashed ' + (file ? '#c6f6d5' : '#cbd5e0'),
                borderRadius: 12,
                padding: '48px 32px',
                textAlign: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                background: file ? '#f0fff4' : 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: file ? '0 4px 12px rgba(56, 161, 105, 0.1)' : '0 2px 8px rgba(0,0,0,0.05)',
                opacity: uploading ? 0.6 : 1
              }}
            >
              <input id="zip-upload" type="file" accept=".zip" onChange={handleFileSelect} disabled={uploading || (status && status.status === 'processing')} style={{ display: 'none' }} />
              <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
              {file ? (
                <div>
                  <p style={{ margin: 0, fontWeight: 700, color: '#38a169', fontSize: 16 }}>{file.name}</p>
                  <p style={{ margin: '8px 0 0', fontSize: 13, color: '#718096' }}>{(file.size / 1024 / 1024).toFixed(1)} MB • Click to change file</p>
                </div>
              ) : (
                <div>
                  <p style={{ margin: 0, fontWeight: 600, color: '#2d3748', fontSize: 15 }}>Drop your ZIP file here or click to browse</p>
                  <p style={{ margin: '8px 0 0', fontSize: 13, color: '#a0aec0' }}>Supports <span style={{ color: '#3182ce', fontWeight: 600 }}>.ZIP</span> files containing <span style={{ color: '#3182ce', fontWeight: 600 }}>PDF</span> documents</p>
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
              <div>
                <label style={{ fontWeight: 600, marginBottom: 8, display: 'block', color: '#2d3748', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>🤖 AI Model</label>
                <select value={model} onChange={(e) => setModel(e.target.value)} disabled={uploading || (status && status.status === 'processing')} style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: '1px solid #cbd5e0',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  <option value="bedrock-vl">AWS Bedrock - Qwen3-VL-235B (Vision)</option>
                  <option value="gemini">Google Gemini (Gemma-4-31B)</option>
                  <option value="openrouter">OpenRouter (Gemma-4-31B)</option>
                  <option value="bedrock">AWS Bedrock - Qwen3-32B (Text)</option>
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 600, marginBottom: 8, display: 'block', color: '#2d3748', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>📋 Report Type</label>
                <select value={reportType} onChange={(e) => setReportType(e.target.value)} disabled={uploading || (status && status.status === 'processing')} style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: '1px solid #cbd5e0',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  <option value="SIP APPLICATION FORM (BAJAJ)">SIP APPLICATION FORM (BAJAJ)</option>
                </select>
              </div>
            </div>

            <button
              onClick={handleUpload}
              disabled={!file || uploading || (status && status.status === 'processing') || redirectCountdown > 0}
              style={{
                width: 'fit-content',
                padding: '14px 32px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: !file || uploading || redirectCountdown > 0 ? '#cbd5e0' : 'linear-gradient(135deg, #3182ce 0%, #2c5aa0 100%)',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 700,
                cursor: !file || uploading || redirectCountdown > 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: !file || uploading || redirectCountdown > 0 ? 'none' : '0 6px 20px rgba(49, 130, 206, 0.4)',
                opacity: !file || uploading || redirectCountdown > 0 ? 0.6 : 1,
                textTransform: 'uppercase',
                letterSpacing: 0.5
              }}
              onMouseEnter={(e) => {
                if (!(!file || uploading || redirectCountdown > 0)) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(49, 130, 206, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = !file || uploading || redirectCountdown > 0 ? 'none' : '0 6px 20px rgba(49, 130, 206, 0.4)';
              }}
            >
              {uploading ? <Loader size={18} className="spin" /> : redirectCountdown > 0 ? <Loader size={18} className="spin" /> : <Play size={18} />}
              {uploading ? 'Uploading...' : redirectCountdown > 0 ? `Redirecting in ${redirectCountdown}s` : 'Start Batch Extraction'}
            </button>

            {error && (
              <div style={{
                padding: 14,
                background: '#FEE2E2',
                border: '1px solid #FECACA',
                borderRadius: 8,
                color: '#991B1B',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: 13,
                fontWeight: 500
              }}>
                <AlertCircle size={18} style={{ flexShrink: 0 }} />
                {error}
              </div>
            )}
          </div>
        </div>

        {/* After upload, show confirmation with loader and countdown */}
        {status && batchId && (
          <div style={{
            marginTop: 24,
            marginBottom: 24,
            padding: 24,
            background: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)',
            border: '2px solid #86EFAC',
            borderRadius: 12,
            boxShadow: '0 8px 20px rgba(56, 161, 105, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 20
          }}>
            <div style={{
              fontSize: 48,
              animation: 'pulse 2s infinite'
            }}>✅</div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 8px 0', color: '#166534', fontSize: 18, fontWeight: 700 }}>Batch Uploaded Successfully!</h3>
              <p style={{ margin: '0 0 8px 0', color: '#22543d', fontSize: 13 }}>
                Batch ID: <code style={{ background: 'rgba(255,255,255,0.6)', padding: '4px 8px', borderRadius: 4, fontWeight: 600, marginLeft: 4 }}>{batchId}</code>
              </p>
              <p style={{ margin: 0, color: '#22543d', fontSize: 12, opacity: 0.8 }}>
                ⏳ Redirecting to batch details in <strong>{redirectCountdown}</strong> seconds...
              </p>
            </div>
            <div style={{
              padding: '20px',
              background: 'rgba(255,255,255,0.4)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '60px',
              minHeight: '60px'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '3px solid rgba(56, 161, 105, 0.3)',
                borderTop: '3px solid #38a169',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
          </div>
        )}

        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.8; }
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>

        {/* Queue Status Section */}
        {queueStatus && (queueStatus.queued > 0 || queueStatus.processing > 0) && (
          <div style={{
            background: 'linear-gradient(135deg, #FEF3C7 0%, #FCD34D 100%)',
            borderRadius: 14,
            padding: 20,
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            border: '2px solid #FBBF24',
            marginTop: 24,
            marginBottom: 24
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 700, color: '#92400E', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 24 }}>⏳</span>
              Batch Queue Status
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 16 }}>
              {/* Currently Processing */}
              <div style={{
                background: 'white',
                padding: 14,
                borderRadius: 8,
                border: '1px solid #FBBF24',
                textAlign: 'center'
              }}>
                <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#92400E', fontWeight: 600, textTransform: 'uppercase' }}>🟡 Processing</p>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#D97706' }}>{queueStatus.processing || 0}</p>
              </div>

              {/* Queued Waiting */}
              <div style={{
                background: 'white',
                padding: 14,
                borderRadius: 8,
                border: '1px solid #FBBF24',
                textAlign: 'center'
              }}>
                <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#92400E', fontWeight: 600, textTransform: 'uppercase' }}>⏸️  Queued</p>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#D97706' }}>{queueStatus.queued || 0}</p>
              </div>

              {/* Completed */}
              <div style={{
                background: 'white',
                padding: 14,
                borderRadius: 8,
                border: '1px solid #FBBF24',
                textAlign: 'center'
              }}>
                <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#92400E', fontWeight: 600, textTransform: 'uppercase' }}>✅ Completed</p>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#D97706' }}>{queueStatus.completed || 0}</p>
              </div>

              {/* Failed */}
              <div style={{
                background: 'white',
                padding: 14,
                borderRadius: 8,
                border: '1px solid #FBBF24',
                textAlign: 'center'
              }}>
                <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#92400E', fontWeight: 600, textTransform: 'uppercase' }}>❌ Failed</p>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#D97706' }}>{queueStatus.failed || 0}</p>
              </div>
            </div>

            {/* Current Batch Info */}
            {queueStatus.currentBatchId && (
              <div style={{
                background: 'white',
                padding: 12,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontSize: 13
              }}>
                <span style={{ fontSize: 20 }}>▶️</span>
                <div>
                  <strong style={{ color: '#92400E' }}>Now Processing:</strong>
                  <code style={{ marginLeft: 8, background: '#FEF3C7', padding: '2px 6px', borderRadius: 4, fontWeight: 600, color: '#B45309' }}>
                    {queueStatus.currentBatchId}
                  </code>
                </div>
              </div>
            )}

            {/* Queued Items List */}
            {queueItems.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#92400E', fontWeight: 600 }}>📋 Queued Batches:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {queueItems.filter(item => item.status === 'queued').map((item, idx) => (
                    <div key={item.batchId} style={{
                      background: 'white',
                      padding: '8px 12px',
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      border: '1px solid #FEF3C7'
                    }}>
                      <span style={{ fontWeight: 700, color: '#D97706', minWidth: 20 }}>#{idx + 1}</span>
                      <code style={{ color: '#92400E', fontWeight: 600 }}>{item.batchId}</code>
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: '#B45309' }}>
                        Added: {new Date(item.createdAt).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Process Next Batch Button */}
            {queueStatus.queued > 0 && queueStatus.processing === 0 && (
              <div style={{ marginTop: 16 }}>
                <button
                  onClick={processNextBatch}
                  disabled={uploading}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    background: uploading ? '#cbd5e0' : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: uploading ? 'none' : '0 4px 12px rgba(5, 150, 105, 0.3)',
                    opacity: uploading ? 0.6 : 1,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}
                  onMouseEnter={(e) => {
                    if (!uploading) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(5, 150, 105, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = uploading ? 'none' : '0 4px 12px rgba(5, 150, 105, 0.3)';
                  }}
                >
                  {uploading ? <Loader size={18} className="spin" /> : <Play size={18} />}
                  {uploading ? 'Processing...' : '▶️ Process Next Batch'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Batch History */}
        {batches.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: 14,
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            marginTop: 24
          }}>
            <div style={{ padding: 24, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a202c', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 24 }}>📋</span>
                Batch History
              </h3>
              <div style={{ position: 'relative', width: 320 }}>
                <input
                  type="text"
                  placeholder="Search by Batch ID..."
                  style={{
                    width: '100%',
                    padding: '10px 14px 10px 36px',
                    border: '1px solid #cbd5e0',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#3182ce'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(49, 130, 206, 0.1)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = '#cbd5e0'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)'; }}
                  onChange={(e) => {
                    const term = e.target.value.trim();
                    clearTimeout(window._batchSearchTimer);
                    window._batchSearchTimer = setTimeout(() => loadBatches(0, term), 500);
                  }}
                />
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#a0aec0', fontSize: 14 }}>🔍</span>
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: 700, color: '#4a5568', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: 700, color: '#4a5568', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Batch ID</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: 700, color: '#4a5568', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Model</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: 700, color: '#4a5568', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Report Type</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: 700, color: '#4a5568', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>📄 PDFs</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: 700, color: '#4a5568', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Created</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: 700, color: '#4a5568', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>⏱️ Runtime</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: 700, color: '#4a5568', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((b) => {
                  const total = b.totalPdfs || 0;
                  const statusColor = b.status === 'PROCESSING' ? { bg: '#FEF3C7', color: '#B45309', badge: '🟡' } :
                                     b.status === 'COMPLETED' ? { bg: '#DCFCE7', color: '#166534', badge: '✅' } :
                                     b.status === 'FAILED' ? { bg: '#FEE2E2', color: '#991B1B', badge: '❌' } :
                                     { bg: '#F3F4F6', color: '#4B5563', badge: '⚪' };

                  return (
                  <tr key={b.batchId} style={{
                    borderBottom: '1px solid #e2e8f0',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: 'transparent'
                  }} onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8f9fa';
                    e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #e2e8f0';
                  }} onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.boxShadow = 'none';
                  }}>
                    <td style={{ padding: '14px 16px', fontWeight: 600 }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '6px 12px',
                        background: statusColor.bg,
                        color: statusColor.color,
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5
                      }}>
                        {statusColor.badge} {b.status || 'UNKNOWN'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <code style={{
                        background: '#f0f4f8',
                        color: '#3182ce',
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontWeight: 600,
                        fontSize: 12
                      }}>
                        {b.batchId}
                      </code>
                    </td>
                    <td style={{ padding: '14px 16px', fontWeight: 500, color: '#4a5568' }}>{b.model || '-'}</td>
                    <td style={{ padding: '14px 16px', color: '#4a5568' }}>{b.reportType || '-'}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 700, fontSize: 14, color: '#2d3748' }}>{total}</td>
                    <td style={{ padding: '14px 16px', fontSize: 12, color: '#718096' }}>{formatDate(b.createdAt)}</td>
                    <td style={{
                      padding: '14px 16px',
                      textAlign: 'center',
                      fontWeight: 600,
                      color: formatRuntimeDisplay(b).color,
                      background: formatRuntimeDisplay(b).bg,
                      borderRadius: '4px',
                      fontSize: 12
                    }}>
                      {formatRuntimeDisplay(b).display}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <button onClick={() => window.location.href = `/batch/${b.batchId}`} style={{
                        background: '#EBF8FF',
                        border: 'none',
                        color: '#3182ce',
                        cursor: 'pointer',
                        padding: '6px 12px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        marginRight: 6,
                        transition: 'all 0.2s'
                      }} onMouseEnter={(e) => { e.currentTarget.style.background = '#BFDBFE'; e.currentTarget.style.color = '#1e40af'; }} onMouseLeave={(e) => { e.currentTarget.style.background = '#EBF8FF'; e.currentTarget.style.color = '#3182ce'; }} title="View details">
                        View
                      </button>
                      <button onClick={() => {
                        const token = localStorage.getItem('token');
                        window.open(`/api/batch/${b.batchId}/report?token=${token}`, '_blank');
                      }} style={{
                        background: 'none',
                        border: 'none',
                        color: '#3182ce',
                        cursor: 'pointer',
                        padding: '6px',
                        borderRadius: 4,
                        marginRight: 4,
                        transition: 'all 0.2s',
                        display: 'inline-flex',
                        alignItems: 'center'
                      }} onMouseEnter={(e) => { e.currentTarget.style.background = '#EBF8FF'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }} title="Download Report">
                        <Download size={14} />
                      </button>
                      <button onClick={() => deleteBatch(b.batchId)} style={{
                        background: 'none',
                        border: 'none',
                        color: '#e53e3e',
                        cursor: 'pointer',
                        padding: '6px',
                        borderRadius: 4,
                        transition: 'all 0.2s',
                        display: 'inline-flex',
                        alignItems: 'center'
                      }} onMouseEnter={(e) => { e.currentTarget.style.background = '#FEE2E2'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }} title="Delete batch">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Pagination */}
            {batchPagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8f9fa' }}>
                <span style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>
                  Page <span style={{ fontWeight: 700, color: '#2d3748' }}>{batchPagination.page}</span> of <span style={{ fontWeight: 700, color: '#2d3748' }}>{batchPagination.pages}</span> • <span style={{ fontWeight: 700, color: '#2d3748' }}>{batchPagination.total}</span> batches total
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => loadBatches(Math.max(0, batchPagination.skip - batchPagination.limit))} disabled={batchPagination.page <= 1} style={{
                    padding: '8px 14px',
                    border: '1px solid #cbd5e0',
                    borderRadius: 6,
                    cursor: batchPagination.page <= 1 ? 'not-allowed' : 'pointer',
                    background: batchPagination.page <= 1 ? '#f3f4f6' : 'white',
                    fontWeight: 600,
                    fontSize: 12,
                    color: batchPagination.page <= 1 ? '#a0aec0' : '#2d3748',
                    transition: 'all 0.2s',
                    opacity: batchPagination.page <= 1 ? 0.5 : 1
                  }} onMouseEnter={(e) => { if (batchPagination.page > 1) { e.currentTarget.style.borderColor = '#3182ce'; e.currentTarget.style.background = '#EBF8FF'; } }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e0'; e.currentTarget.style.background = 'white'; }}>
                    ← Prev
                  </button>
                  <button onClick={() => loadBatches(batchPagination.skip + batchPagination.limit)} disabled={batchPagination.page >= batchPagination.pages} style={{
                    padding: '8px 14px',
                    border: '1px solid #cbd5e0',
                    borderRadius: 6,
                    cursor: batchPagination.page >= batchPagination.pages ? 'not-allowed' : 'pointer',
                    background: batchPagination.page >= batchPagination.pages ? '#f3f4f6' : 'white',
                    fontWeight: 600,
                    fontSize: 12,
                    color: batchPagination.page >= batchPagination.pages ? '#a0aec0' : '#2d3748',
                    transition: 'all 0.2s',
                    opacity: batchPagination.page >= batchPagination.pages ? 0.5 : 1
                  }} onMouseEnter={(e) => { if (batchPagination.page < batchPagination.pages) { e.currentTarget.style.borderColor = '#3182ce'; e.currentTarget.style.background = '#EBF8FF'; } }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e0'; e.currentTarget.style.background = 'white'; }}>
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchExtract;
